/**
 * Pure logic for the body-measurement import read-window: where to start reading
 * (a persisted high-water mark, else a bounded first-connect backfill) and how to
 * advance the mark. UI-free and I/O-free so it runs under the app's `test:unit`
 * project — the hook owns the AsyncStorage read/write and hands values here.
 */
export class ImportWindow {
  /** First-connect backfill span — read this far back rather than all of history. */
  static readonly BACKFILL_MONTHS = 12;

  /**
   * Start of the read window. A valid stored high-water mark (max `measuredAt`
   * imported so far, as epoch millis) wins; otherwise fall back to a bounded
   * backfill window ending at `now`.
   */
  static resolveStart(now: Date, storedMark: string | null): Date {
    const ts = storedMark ? Number(storedMark) : NaN;
    if (Number.isFinite(ts) && ts > 0) return new Date(ts);

    const start = new Date(now);
    start.setMonth(start.getMonth() - ImportWindow.BACKFILL_MONTHS);
    return start;
  }

  /**
   * Advance the high-water mark for one imported sample. Clamps the sample time
   * to `now` so a future-dated device clock can't push the mark past real
   * readings that follow it.
   */
  static nextMark(
    previous: number,
    sampleMillis: number,
    nowMillis: number,
  ): number {
    return Math.max(previous, Math.min(sampleMillis, nowMillis));
  }
}
